import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { postInsuranceCardUrl } from 'src/app/client/api';
import * as actionTypes from 'src/app/state/actions/types';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { LegacyInsurance } from 'src/app/types';
import { extractErrorCodes } from 'src/app/utils';

import { segmentTrack } from '.';

export const uploadInsuranceRequest = () => {
  return {
    type: actionTypes.UPLOAD_INSURANCE_REQUEST,
  };
};

export const uploadInsuranceSuccess = () => {
  return {
    type: actionTypes.UPLOAD_INSURANCE_SUCCESS,
  };
};

export const uploadInsuranceFailure = error => {
  return {
    type: actionTypes.UPLOAD_INSURANCE_FAILURE,
    error,
  };
};

export function setInsuranceCards(accountId, frontFile, backFile) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    dispatch(uploadInsuranceRequest());
    try {
      client.postInsuranceCard(accountId, frontFile, backFile);
      dispatch(uploadInsuranceSuccess());
      dispatch(segmentTrack(UNIFIED.insuranceSuccessful));
      return true;
    } catch (err) {
      const bffError = err?.error ?? '';
      const api = postInsuranceCardUrl(accountId);
      const trackErrorPayload = {
        errorMessage: 'Upload Insurance Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(err),
        source: window.location.href,
      };
      segmentTrack(UNIFIED.error, trackErrorPayload);
      dispatch(segmentTrack(UNIFIED.insuranceFailed, trackErrorPayload));
      dispatch(uploadInsuranceFailure(err?.response));
      return null;
    }
  };
}

export function deleteInsuranceCard(id, patient_id) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    return client.deleteInsurance(id, patient_id);
  };
}

export function fetchedInsuranceSuccess(data) {
  return {
    type: actionTypes.FETCHED_INSURANCE_SUCCESS,
    data,
  };
}

export function fetchingInsurance() {
  return {
    type: actionTypes.FETCHING_INSURANCE,
  };
}

export const getInsurance = (accountId: string) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    dispatch(fetchingInsurance());
    const response = await client.getInsurance(accountId);
    const { activeInsurances, pendingInsurance } = response?.data || {};
    dispatch(fetchedPendingInsuranceSuccess(pendingInsurance));
    dispatch(fetchedInsuranceSuccess(activeInsurances));
  };
};

export function fetchedPendingInsuranceSuccess(attributes) {
  return {
    type: actionTypes.FETCHED_PENDING_INSURANCE_SUCCESS,
    attributes,
  };
}

// Load the added insurance into the store optimistically, request real pending insurance on refresh

export const optimisticSetPendingInsurance = () => {
  const now = new Date();
  const attributes = {
    submittedOn: now.toISOString(),
    pending: true,
  };
  return {
    type: actionTypes.FETCHED_PENDING_INSURANCE_SUCCESS,
    attributes,
  };
};

/**
 * Void a primary insurance
 * @param insuredId UUID string. 1-to-1 mapping from a Capsule Patient ID
 */
export function voidPrimaryInsurance(insuredId: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({ type: actionTypes.VOID_PRIMARY_INSURANCE_REQUEST });

    try {
      const { data }: { data: LegacyInsurance } = await client.bffVoidPrimaryInsurance(insuredId);
      dispatch({ type: actionTypes.VOID_PRIMARY_INSURANCE_SUCCESS, data });
    } catch (error) {
      dispatch({ type: actionTypes.VOID_PRIMARY_INSURANCE_FAILURE, error });
    }
  };
}

/**
 * Void a secondary insurance
 * @param insuredId UUID string. 1-to-1 mapping from a Capsule Patient ID
 * @param bin aka 'Payer Bin'
 * @param cardholderNumber aka 'Member Id'
 */
export function voidSecondaryInsurance(insuredId: string, bin: string, cardholderNumber: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({ type: actionTypes.VOID_SECONDARY_INSURANCE_REQUEST });

    try {
      const { data }: { data: LegacyInsurance } = await client.bffVoidSecondaryInsurance(
        insuredId,
        bin,
        cardholderNumber
      );
      dispatch({ type: actionTypes.VOID_SECONDARY_INSURANCE_SUCCESS, data });
    } catch (error) {
      dispatch({ type: actionTypes.VOID_SECONDARY_INSURANCE_FAILURE, error });
    }
  };
}
